/**
 * useTaskFilesProgress Hook
 *
 * REST initial load + WebSocket live per-page updates.
 * Slow REST refresh every 15s for boolean flag transitions.
 * Stops when all_ready === true or enabled === false.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import type { TaskFilesProcessingResponse, TaskFileProcessingInfo, FileProgressWsMessage } from '@/types/tasks';
import { getFileProcessingPercent, estimateFileEta } from '@/types/tasks';
import { getTaskFilesProcessingStatus } from '@/services/api';

const REST_REFRESH_INTERVAL_MS = 15000;

export interface UseTaskFilesProgressReturn {
  data: TaskFilesProcessingResponse | null;
  liveProgress: Map<string, FileProgressWsMessage>;
  isLoading: boolean;
  error: string | null;
  overallPercent: number;
  totalEtaSeconds: number;
  allReady: boolean;
  refresh: () => Promise<TaskFilesProcessingResponse | null>;
}

function computeOverallPercent(
  files: TaskFileProcessingInfo[],
  liveProgress: Map<string, FileProgressWsMessage>
): number {
  if (files.length === 0) return 100;
  const sum = files.reduce(
    (acc, f) => acc + getFileProcessingPercent(f, liveProgress.get(f.file_id) ?? null),
    0
  );
  return Math.round(sum / files.length);
}

function computeTotalEta(files: TaskFileProcessingInfo[]): number {
  if (files.length === 0) return 0;
  // Files process in parallel — take the max
  return Math.max(...files.map(f => estimateFileEta(f)), 0);
}

export function useTaskFilesProgress(
  taskId: string | null,
  enabled: boolean = true
): UseTaskFilesProgressReturn {
  const [data, setData] = useState<TaskFilesProcessingResponse | null>(null);
  const [liveProgress, setLiveProgress] = useState<Map<string, FileProgressWsMessage>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const mountedRef = useRef(true);
  const fileIdsRef = useRef<string[]>([]);

  const fetchStatus = useCallback(async (): Promise<TaskFilesProcessingResponse | null> => {
    if (!taskId) return null;

    try {
      const result = await getTaskFilesProcessingStatus(taskId);
      if (mountedRef.current) {
        setData(result);
        setError(null);
      }
      return result as TaskFilesProcessingResponse;
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to fetch file processing status');
      }
      return null;
    }
  }, [taskId]);

  const connectWs = useCallback((fileIds: string[]) => {
    if (fileIds.length === 0) return;

    const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const ws = new WebSocket(`${protocol}//${location.host}/ws/file-processing`);
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({ type: 'subscribe', file_ids: fileIds }));
    };

    ws.onmessage = (event: MessageEvent) => {
      try {
        const msg = JSON.parse(event.data as string) as FileProgressWsMessage;
        if (msg.type === 'file_progress') {
          setLiveProgress(prev => {
            const current = prev.get(msg.file_id);
            // Guard against out-of-order messages
            if (current && current.stage === msg.stage && current.pages_done > msg.pages_done) {
              return prev;
            }
            const next = new Map(prev);
            next.set(msg.file_id, msg);
            return next;
          });
        }
      } catch {
        // Ignore malformed messages
      }
    };

    ws.onclose = () => {
      // Reconnect after 3s if still mounted and not all ready
      if (mountedRef.current && fileIdsRef.current.length > 0) {
        setTimeout(() => {
          if (mountedRef.current && fileIdsRef.current.length > 0) {
            connectWs(fileIdsRef.current);
          }
        }, 3000);
      }
    };
  }, []);

  // Initial REST load + WS connect
  useEffect(() => {
    mountedRef.current = true;

    if (!taskId || !enabled) {
      setData(null);
      setError(null);
      setLiveProgress(new Map());
      fileIdsRef.current = [];
      return;
    }

    setIsLoading(true);
    fetchStatus().then(result => {
      if (mountedRef.current) {
        setIsLoading(false);
        if (result && !result.all_ready) {
          const ids = result.files.map(f => f.file_id);
          fileIdsRef.current = ids;
          connectWs(ids);
        }
      }
    });

    return () => {
      mountedRef.current = false;
      fileIdsRef.current = [];
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [taskId, enabled, fetchStatus, connectWs]);

  // Slow REST refresh every 15s for boolean flag transitions
  useEffect(() => {
    if (!taskId || !enabled || data?.all_ready) return;

    const interval = setInterval(() => {
      fetchStatus();
    }, REST_REFRESH_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [taskId, enabled, data?.all_ready, fetchStatus]);

  // Stop WS when all_ready
  useEffect(() => {
    if (data?.all_ready) {
      fileIdsRef.current = [];
      wsRef.current?.close();
      wsRef.current = null;
    }
  }, [data?.all_ready]);

  const overallPercent = data ? computeOverallPercent(data.files, liveProgress) : 0;
  const totalEtaSeconds = data ? computeTotalEta(data.files) : 0;
  const allReady = data?.all_ready ?? false;

  return {
    data,
    liveProgress,
    isLoading,
    error,
    overallPercent,
    totalEtaSeconds,
    allReady,
    refresh: fetchStatus,
  };
}

export default useTaskFilesProgress;
